'use client';

import React, { useEffect, useMemo, useRef } from 'react';
import type { NumberLinePayload } from './types';
import { loadJSXGraph } from '@/lib/loadJSXGraph';

interface NumberLineEntryProps {
  payload: NumberLinePayload;
}

function sanitizePayload(payload: NumberLinePayload): Required<NumberLinePayload> {
  const min = Number.isFinite(payload.min) ? payload.min : -10;
  const max = Number.isFinite(payload.max) ? payload.max : 10;
  const tickStep = payload.tickStep && payload.tickStep > 0 ? payload.tickStep : 1;
  return {
    min,
    max: max > min ? max : min + 1,
    tickStep,
    points: payload.points ?? [],
    intervals: payload.intervals ?? [],
  };
}

export function NumberLineEntry({ payload }: NumberLineEntryProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<any>(null);
  const boardId = useMemo(() => `jxg-numberline-${Math.random().toString(36).slice(2)}`, []);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      if (!containerRef.current) return;

      const data = sanitizePayload(payload);
      const JXG = await loadJSXGraph();
      if (!isMounted || !containerRef.current) return;

      if (boardRef.current) {
        JXG.JSXGraph.freeBoard(boardRef.current);
        boardRef.current = null;
      }

      const board = JXG.JSXGraph.initBoard(boardId, {
        boundingbox: [data.min - 0.8, 1.6, data.max + 0.8, -1.6],
        axis: false,
        grid: false,
        showNavigation: false,
        showCopyright: false,
        pan: { enabled: false },
        zoom: { enabled: false },
      });
      boardRef.current = board;

      const axis = board.create('line', [[data.min, 0], [data.max, 0]], {
        straightFirst: false,
        straightLast: false,
        strokeColor: '#4b5563',
        strokeWidth: 2,
        fixed: true,
        highlight: false,
      });

      for (let tick = data.min; tick <= data.max + 1e-9; tick += data.tickStep) {
        const x = Number(tick.toFixed(8));
        board.create('segment', [[x, -0.08], [x, 0.08]], {
          strokeColor: '#9ca3af',
          strokeWidth: 1,
          fixed: true,
          highlight: false,
        });
        board.create('text', [x, -0.28, String(x)], {
          anchorX: 'middle',
          fontSize: 12,
          color: '#374151',
          fixed: true,
          highlight: false,
        });
      }

      data.intervals.forEach((interval, index) => {
        const y = 0.35 + index * 0.16;
        const start = Math.max(data.min, Math.min(data.max, interval.start));
        const end = Math.max(data.min, Math.min(data.max, interval.end));
        const x1 = Math.min(start, end);
        const x2 = Math.max(start, end);

        board.create('segment', [[x1, y], [x2, y]], {
          strokeColor: '#1d4ed8',
          strokeWidth: 4,
          fixed: true,
          highlight: false,
        });

        board.create('point', [x1, y], {
          name: '',
          size: 4,
          face: 'o',
          fillColor: interval.startInclusive ? '#1d4ed8' : '#ffffff',
          strokeColor: '#1d4ed8',
          fixed: true,
          highlight: false,
        });

        board.create('point', [x2, y], {
          name: '',
          size: 4,
          face: 'o',
          fillColor: interval.endInclusive ? '#1d4ed8' : '#ffffff',
          strokeColor: '#1d4ed8',
          fixed: true,
          highlight: false,
        });

        if (interval.label) {
          board.create('text', [(x1 + x2) / 2, y + 0.18, interval.label], {
            anchorX: 'middle',
            color: '#1d4ed8',
            fontSize: 12,
            fixed: true,
            highlight: false,
          });
        }
      });

      data.points.forEach((point) => {
        board.create('point', [point.x, 0], {
          name: point.label || '',
          size: 4,
          face: 'o',
          fillColor: point.style === 'hollow' ? '#ffffff' : '#1f2937',
          strokeColor: '#1f2937',
          fixed: true,
          highlight: false,
          label: {
            offset: [0, -16],
            cssStyle: 'color:#3f3f46;font-size:12px;',
          },
        });
      });

      board.update();
      axis.setAttribute({ fixed: true });
    };

    void init();

    return () => {
      isMounted = false;
      if (boardRef.current && window.JXG?.JSXGraph) {
        window.JXG.JSXGraph.freeBoard(boardRef.current);
        boardRef.current = null;
      }
    };
  }, [boardId, payload]);

  return <div id={boardId} ref={containerRef} style={{ width: '100%', height: 150, backgroundColor: '#ffffff' }} />;
}
